import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleAuthMutation } from '../../../store/api/authApi';
import { authErrorMessage, GOOGLE_FAILED_MESSAGE } from '../../../store/api/authHelpers';
import { nextFromOAuthState, takeGoogleRemember } from '../../../utilities/googleAuth';
import TwoFactorDialog from '../SignIn/TwoFactorDialog';
import { t } from '../../../i18n/i18n';
import './GoogleCallback.scss';

/**
 * Where Google drops the trader after the consent screen (`/auth/google`). It
 * sits outside both route guards: the session doesn't exist yet when this
 * mounts, and once the exchange lands, PublicRoute would bounce the user to the
 * dashboard before we could honour the `next` they were originally heading for.
 */
export default function GoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [googleAuth] = useGoogleAuthMutation();
  const [error, setError] = useState(null);
  /**
   * An account with 2FA on comes back with a `temp_token` instead of a
   * session — the same challenge the email sign-in opens, spent here.
   */
  const [twoFactor, setTwoFactor] = useState(null);

  const goOn = () =>
    navigate(nextFromOAuthState(searchParams.get('state')) ?? '/dashboard', { replace: true });

  /**
   * The code is single-use, so the exchange must not run twice — which
   * StrictMode's double-mounted effects would otherwise do in development.
   */
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const code = searchParams.get('code');
    if (!code) {
      // Google reports a refused or cancelled consent as `error` instead.
      setError(
        searchParams.get('error_description') ||
          (searchParams.get('error') ? 'Google sign-in was cancelled.' : GOOGLE_FAILED_MESSAGE),
      );
      return;
    }

    // The checkbox the trader ticked before leaving for Google, read once.
    const remember = takeGoogleRemember();
    googleAuth({ code, remember })
      .unwrap()
      .then((data) => {
        if (data?.requires_2fa) {
          if (data.temp_token) setTwoFactor({ tempToken: data.temp_token, remember });
          else setError(GOOGLE_FAILED_MESSAGE);
          return;
        }
        if (!data?.idToken) {
          setError(GOOGLE_FAILED_MESSAGE);
          return;
        }
        goOn();
      })
      .catch((err) => setError(authErrorMessage(err)));
    // Deliberately runs once, on the code present in the URL at mount — the
    // `exchanged` ref above is what actually enforces that.
  }, [googleAuth, navigate, searchParams]);

  return (
    <div className="google-callback">
      {error ? (
        <div className="google-callback__panel" role="alert">
          <h1 className="google-callback__title">{t('Sign-in failed')}</h1>
          <p className="google-callback__message">{t(error)}</p>
          <Link to="/sign-in" className="google-callback__link">
            {t('Back to sign in')}
          </Link>
        </div>
      ) : (
        <div className="google-callback__panel">
          <span className="google-callback__spinner" aria-hidden="true" />
          <p className="google-callback__message">
            {twoFactor ? t('Two-factor authentication') : t('Verifying your Google account…')}
          </p>
        </div>
      )}

      <TwoFactorDialog
        open={Boolean(twoFactor)}
        tempToken={twoFactor?.tempToken}
        remember={twoFactor?.remember}
        // No form to return to here: cancelling goes back to sign in.
        onCancel={() => {
          setTwoFactor(null);
          navigate('/sign-in', { replace: true });
        }}
        onVerified={() => {
          setTwoFactor(null);
          goOn();
        }}
      />
    </div>
  );
}
